import { fabric } from "fabric";
import { v4 as uuidv4 } from "uuid";
import { CurvedText } from "./curved-text";

import {
  CustomFabricObject,
  ElementDirection,
  ImageUpload,
  ModifyShape,
} from "../types/type";
import { getConsumerLocksObject } from "../constants";
import { preloadedFonts } from "../constants/fonts";
import {
  captureCanvasObjectOriginalCenter,
  recenterTextToOriginalCenter,
} from "./canvas";
import { getLayerNameByType, notify } from "./utils";

export const createRectangle = (pointer: PointerEvent) => {
  const rect = new fabric.Rect({
    left: pointer.x,
    top: pointer.y,
    width: 100,
    height: 100,
    fill: "#aabbcc",
    objectId: uuidv4(),
    consumerLocks: getConsumerLocksObject("rect"),
    layerName: getLayerNameByType("rect"),
    strokeWidth: 0,
  } as CustomFabricObject<fabric.Rect>);

  return rect;
};

export const createTriangle = (pointer: PointerEvent) => {
  return new fabric.Triangle({
    left: pointer.x,
    top: pointer.y,
    width: 100,
    height: 100,
    fill: "#aabbcc",
    objectId: uuidv4(),
    consumerLocks: getConsumerLocksObject("triangle"),
    layerName: getLayerNameByType("triangle"),
    strokeWidth: 0,
  } as CustomFabricObject<fabric.Triangle>);
};

export const createCircle = (pointer: PointerEvent) => {
  return new fabric.Circle({
    left: pointer.x,
    top: pointer.y,
    radius: 100,
    fill: "#aabbcc",
    objectId: uuidv4(),
    consumerLocks: getConsumerLocksObject("circle"),
    layerName: getLayerNameByType("circle"),
    strokeWidth: 0,
  } as any);
};

export const createLine = (pointer: PointerEvent) => {
  return new fabric.Line(
    [pointer.x, pointer.y, pointer.x + 100, pointer.y + 100],
    {
      stroke: "#aabbcc",
      fill: "#aabbcc",
      strokeWidth: 2,
      objectId: uuidv4(),
      consumerLocks: getConsumerLocksObject("line"),
      layerName: getLayerNameByType("line"),
    } as CustomFabricObject<fabric.Line>
  );
};

export const createText = (pointer: PointerEvent, text: string) => {
  const textObj = new fabric.IText(text, {
    left: pointer.x,
    top: pointer.y,
    fill: "#aabbcc",
    fontFamily: preloadedFonts[0].fontFamily,
    fontSize: 36,
    fontWeight: "400",
    objectId: uuidv4(),
    consumerLocks: getConsumerLocksObject("i-text"),
    layerName: getLayerNameByType("i-text"),
    strokeWidth: 0,
    textAlign: "center",
  } as fabric.ITextOptions);

  return textObj;
};

export const createCurvedText = (pointer: PointerEvent, text: string) => {
  const curvedText = new CurvedText(text, {
    left: pointer.x,
    top: pointer.y,
    fill: "#aabbcc",
    fontFamily: preloadedFonts[0].fontFamily,
    fontSize: 36,
    fontWeight: "400",
    objectId: uuidv4(),
    consumerLocks: getConsumerLocksObject("curved-text"), // curved-text is not a type, but has different locks.
    layerName: getLayerNameByType("curved-text"),
    strokeWidth: 0,
    textAlign: "center",
  });

  return curvedText;
};

export const createSpecificShape = (
  shapeType: string,
  pointer: PointerEvent
) => {
  switch (shapeType) {
    case "rectangle":
      return createRectangle(pointer);

    case "triangle":
      return createTriangle(pointer);

    case "circle":
      return createCircle(pointer);

    case "line":
      return createLine(pointer);

    case "text":
      return createText(pointer, "Tap to Type");

    case "curvedText":
      return createCurvedText(pointer, "Curve Here");

    default:
      return null;
  }
};

export const handleImageUpload = ({
  file,
  canvas,
  shapeRef,
  syncShapeInStorage,
}: ImageUpload) => {
  const reader = new FileReader();

  reader.onload = () => {
    console.log("File type:", file.type); // Log the file type

    console.log("reader.result", reader.result);

    if (file.type.includes("svg")) {
      console.log("SVG file detected IS DETECTED");

      fabric.loadSVGFromURL(
        reader.result as string,
        function (objects, options) {
          // check if the svg is corrupted with NAN values.
          let isSvgCorrupted = false;

          // filter svg objects that are corrupted with NAN values.
          objects = objects.filter((obj) => {
            if (obj.strokeWidth && !obj.stroke) {
              obj.strokeWidth = 0;
            }

            // might be width, height, left, top or any other property.
            if (
              // @ts-ignore
              isNaN(obj.width) ||
              // @ts-ignore
              isNaN(obj.height) ||
              // @ts-ignore
              isNaN(obj.left) ||
              // @ts-ignore
              isNaN(obj.top)
            ) {
              isSvgCorrupted = true;
              return false;
            }

            return true;
          });

          if (isSvgCorrupted) {
            notify({
              message:
                "SVG file has corrupted object that could not be loaded and were skipped. Please try another file.",
              type: "error",
            });
          }

          const group = new fabric.Group(objects, options);
          // @ts-ignore
          group.objectId = uuidv4();
          // @ts-ignore
          group.consumerLocks = getConsumerLocksObject("group");
          // @ts-ignore
          group.layerName = getLayerNameByType("group"); // group layer name

          group.scaleToHeight(
            canvas.current.getHeight() - (10 / 100) * canvas.current.getHeight()
          );

          canvas.current.add(group);

          group.center();
          group.setCoords();

          const items = group._objects;

          // add object ids and consumer locks to object,
          // when calling toActiveSelection, the group will be ungrouped and
          // these objects must have ids and consumer locks.
          items.forEach((item) => {
            // @ts-ignore
            item.objectId = uuidv4();
            // @ts-ignore
            item.consumerLocks = getConsumerLocksObject("svg");
            // @ts-ignore
            item.layerName = getLayerNameByType("svg"); // svg layer name

            // add to storage
            // syncShapeInStorage(item);
          });

          // ungroup the group
          // group.toActiveSelection();
          // add the group to storage
          syncShapeInStorage(group);

          canvas.current.requestRenderAll();
        }
      );
    } else {
      fabric.Image.fromURL(reader.result as string, (img) => {
        img.scaleToWidth(200);
        img.scaleToHeight(200);

        canvas.current.add(img);

        // @ts-ignore
        img.objectId = uuidv4();
        // @ts-ignore
        img.consumerLocks = getConsumerLocksObject("image");
        // @ts-ignore
        img.layerName = getLayerNameByType("image"); // image layer name

        shapeRef.current = img;

        syncShapeInStorage(img);
        canvas.current.requestRenderAll();
      });
    }
  };

  reader.readAsDataURL(file);
};

export const createShape = (
  canvas: fabric.Canvas,
  pointer: PointerEvent,
  shapeType: string
) => {
  if (shapeType === "freeform") {
    canvas.isDrawingMode = true;
    return null;
  }

  return createSpecificShape(shapeType, pointer);
};

export const modifyShape = ({
  canvas,
  property,
  value,
  activeObjectRef,
  syncShapeInStorage,
}: ModifyShape) => {
  const selectedElement = canvas.getActiveObject();

  if (!selectedElement || selectedElement?.type === "activeSelection") return;

  // FIX: values are coming as string, which is wrong.

  // if  property is width or height, set the scale of the selected element
  if (property === "width") {
    selectedElement.scaleToWidth(Number(value), true);
  } else if (property === "height") {
    selectedElement.scaleToHeight(Number(value), true);
  } else if (property === "angle") {
    // rotate here, don't set it
    selectedElement.rotate(Number(value));
  } else if (property === "text") {
    // if we are updating the text value, i-text or curvy for later.
    // get original position of text.

    if (selectedElement.type !== "i-text") return;

    // @ts-ignore
    const consumerLocks = selectedElement.consumerLocks as any;

    // if the update in place lock is there, and true, do re-centering.

    if (consumerLocks.updateTextInPlace === true) {
      // calculate the original center of the text.
      const originalCenter = captureCanvasObjectOriginalCenter(selectedElement);

      // modify shape.
      selectedElement.set(property as any, value);

      // reposition the text to the original position.
      recenterTextToOriginalCenter(selectedElement, originalCenter, canvas);
    } else {
      selectedElement.set(property as any, value);
    }
  } else if (property === "fontSize") {
    // if we are updating the font size, i-text or curvy for later.
    // get original position of text.

    if (selectedElement.type !== "i-text") return;

    // @ts-ignore
    const consumerLocks = selectedElement.consumerLocks as any;

    // if the update in place lock is there, and true, do re-centering.

    if (consumerLocks.updateTextFontSizeInPlace === true) {
      // calculate the original center of the text.
      const originalCenter = captureCanvasObjectOriginalCenter(selectedElement);

      // modify shape.
      selectedElement.set(property as any, value);

      // reposition the text to the original position.
      recenterTextToOriginalCenter(selectedElement, originalCenter, canvas);
    } else {
      selectedElement.set(property as any, value);
    }
  } else if (property === "fontFamily") {
    // if we are updating the font family, i-text or curvy for later.
    // get original position of text.

    if (selectedElement.type !== "i-text") return;

    // @ts-ignore
    const consumerLocks = selectedElement.consumerLocks as any;

    // if the update in place lock is there, and true, do re-centering.

    if (consumerLocks.updateTextFontFamilyInPlace === true) {
      // calculate the original center of the text.
      const originalCenter = captureCanvasObjectOriginalCenter(selectedElement);

      // modify shape.
      selectedElement.set(property as any, value);

      // reposition the text to the original position.
      recenterTextToOriginalCenter(selectedElement, originalCenter, canvas);
    } else {
      selectedElement.set(property as any, value);
    }
  } else {
    if (selectedElement[property as keyof object] === value) return;
    selectedElement.set(property as keyof object, value);
  }

  selectedElement.setCoords();

  canvas.requestRenderAll();

  // set selectedElement to activeObjectRef
  activeObjectRef.current = selectedElement;

  syncShapeInStorage(selectedElement);

  // can be needed in some cases to re-update the element attributes
  return selectedElement;
};

export const updateShapeLayerName = ({
  canvas,
  objectId,
  newLayerName,
  syncShapeInStorage,
}: any) => {
  // this should handle group objects as nested as they can be.
  // find recursively the target object. and update the consumer locks.
  // then find recursively the topmost parent group, for syncing the storage.

  // Helper function to search recursively for the target object
  const findAndModifyObject = (objects: any[], targetId: string): any => {
    for (const obj of objects) {
      if (obj.objectId === targetId) {
        // Update the target object's consumer locks
        obj.layerName = newLayerName;
        return obj; // Return the modified object
      }
      if (obj.type === "group" && obj._objects) {
        const found = findAndModifyObject(obj._objects, targetId);
        if (found) return found; // Return if found in nested group
      }
    }
    return null; // Not found
  };

  // Helper function to find the topmost parent group's ID
  const findTopmostParentGroupId = (obj: any): string | null => {
    if (!obj.group) return obj.objectId; // No parent group, return current object's ID
    return findTopmostParentGroupId(obj.group); // Traverse upward
  };

  // Check for active selection and discard if needed.
  // active selection and find most parent group, is buggy.
  // fabric mid-execution, after finding target and updating it.
  // is passing the active selection to the findTopmostParentGroupId function,
  // even though from outside is target object and not active selection.

  let activeSelectionDiscardedObjectIds: string[] = []; // will save
  // the object ids of the objects that were in the active selection and were discarded.

  const activeObject = canvas.getActiveObject();
  if (activeObject && activeObject.type === "activeSelection") {
    // save
    activeSelectionDiscardedObjectIds = activeObject._objects.map(
      (obj: any) => obj.objectId
    );

    canvas.discardActiveObject();
  }

  // Get all objects from the canvas
  const objects = canvas.getObjects();

  // Find and modify the target object within the hierarchy
  const targetObject = findAndModifyObject(objects, objectId);

  if (!targetObject) {
    console.error("Target object not found");
    return null;
  }

  // Find the topmost parent group ID
  const topmostParentGroupId = findTopmostParentGroupId(targetObject);

  if (!topmostParentGroupId) {
    console.error("Topmost parent group ID not found");
    return null;
  }

  // Retrieve the fresh instance of the topmost parent group from the canvas
  const topmostParentGroup = canvas
    .getObjects()
    // @ts-ignore
    .find((obj) => obj.objectId === topmostParentGroupId);

  if (!topmostParentGroup) {
    console.error("Failed to retrieve topmost parent group from canvas");
    return null;
  }

  // Sync the fresh topmost parent group with storage
  syncShapeInStorage(topmostParentGroup);

  // recreate the active selection if it was discarded.
  if (activeSelectionDiscardedObjectIds.length > 0) {
    const objects = canvas.getObjects();
    const filtered = objects.filter((obj: any) =>
      activeSelectionDiscardedObjectIds.includes(obj.objectId)
    );
    const newSelection = new fabric.ActiveSelection(filtered, {
      canvas: canvas,
    });

    newSelection.setCoords();

    canvas.setActiveObject(newSelection);
  }

  canvas.requestRenderAll();

  return topmostParentGroup;
};

export const updateShapeConsumerLocks = ({
  canvas,
  objectId,
  newConsumerLocksObj,
  syncShapeInStorage,
}: any) => {
  // this should handle group objects as nested as they can be.
  // find recursively the target object. and update the consumer locks.
  // then find recursively the topmost parent group, for syncing the storage.

  // Helper function to search recursively for the target object
  const findAndModifyObject = (objects: any[], targetId: string): any => {
    for (const obj of objects) {
      if (obj.objectId === targetId) {
        // Update the target object's consumer locks
        obj.consumerLocks = { ...obj.consumerLocks, ...newConsumerLocksObj };
        return obj; // Return the modified object
      }
      if (obj.type === "group" && obj._objects) {
        const found = findAndModifyObject(obj._objects, targetId);
        if (found) return found; // Return if found in nested group
      }
    }
    return null; // Not found
  };

  // Helper function to find the topmost parent group's ID
  const findTopmostParentGroupId = (obj: any): string | null => {
    if (!obj.group) return obj.objectId; // No parent group, return current object's ID
    return findTopmostParentGroupId(obj.group); // Traverse upward
  };

  // Check for active selection and discard if needed.
  // active selection and find most parent group, is buggy.
  // fabric mid-execution, after finding target and updating it.
  // is passing the active selection to the findTopmostParentGroupId function,
  // even though from outside is target object and not active selection.

  let activeSelectionDiscardedObjectIds: string[] = []; // will save
  // the object ids of the objects that were in the active selection and were discarded.

  const activeObject = canvas.getActiveObject();
  if (activeObject && activeObject.type === "activeSelection") {
    // save
    activeSelectionDiscardedObjectIds = activeObject._objects.map(
      (obj: any) => obj.objectId
    );

    canvas.discardActiveObject();
  }

  // Get all objects from the canvas
  const objects = canvas.getObjects();

  // Find and modify the target object within the hierarchy
  const targetObject = findAndModifyObject(objects, objectId);

  if (!targetObject) {
    console.error("Target object not found");
    return null;
  }

  // Find the topmost parent group ID
  const topmostParentGroupId = findTopmostParentGroupId(targetObject);

  if (!topmostParentGroupId) {
    console.error("Topmost parent group ID not found");
    return null;
  }

  // Retrieve the fresh instance of the topmost parent group from the canvas
  const topmostParentGroup = canvas
    .getObjects()
    // @ts-ignore
    .find((obj) => obj.objectId === topmostParentGroupId);

  if (!topmostParentGroup) {
    console.error("Failed to retrieve topmost parent group from canvas");
    return null;
  }

  // Sync the fresh topmost parent group with storage
  syncShapeInStorage(topmostParentGroup);

  // recreate the active selection if it was discarded.
  if (activeSelectionDiscardedObjectIds.length > 0) {
    const objects = canvas.getObjects();
    const filtered = objects.filter((obj: any) =>
      activeSelectionDiscardedObjectIds.includes(obj.objectId)
    );
    const newSelection = new fabric.ActiveSelection(filtered, {
      canvas: canvas,
    });

    newSelection.setCoords();

    canvas.setActiveObject(newSelection);
  }

  canvas.requestRenderAll();

  return topmostParentGroup;
};

export const bringElement = ({
  canvas,
  direction, // front, back, forward, backward.
  // front and back across all objects, forward and backward is single step move.
  reorderCanvasObjects,
  saveStateToHistory,
}: ElementDirection) => {
  if (!canvas) return;

  const objIdsBefore = canvas
    .getObjects()
    .map((obj: any) => obj.objectId)
    .join();

  // get the selected element. If there is no selected element or there are more than one selected element, return
  const selectedElement = canvas.getActiveObject();

  if (!selectedElement || selectedElement?.type === "activeSelection") return;

  if (direction === "front") {
    canvas.bringToFront(selectedElement); // at the top of all objects.
  } else if (direction === "back") {
    canvas.sendToBack(selectedElement); // at the bottom of all objects.
  } else if (direction === "forward") {
    canvas.bringForward(selectedElement); // one step forward.
  } else if (direction === "backward") {
    canvas.sendBackwards(selectedElement); // one step backward.
  }

  canvas.renderAll();

  // check the order of the objects after the re-ordering in canvas.
  const objIdsAfter = canvas
    .getObjects()
    .map((obj: any) => obj.objectId)
    .join();

  const isSameOrder = objIdsBefore === objIdsAfter;

  // prevent re-ordering in state, if the order is the same.
  if (!isSameOrder) {
    // canvas objects re-order themselves. call reorder function to update the state.
    reorderCanvasObjects(canvas.getObjects());
    saveStateToHistory?.(); // if there is a saveStateToHistory function, call it.
  }
};

export const handleGroupObjects = ({
  fabricRef,

  syncShapeInStorage,
  deleteShapeFromStorage,
  suppressHistoryRef,
  saveStateToHistory,
}: any) => {
  const canvas = fabricRef.current;

  if (!canvas) return;

  // Get the selected element. If there is no selected element, return
  const selectedElement = canvas.getActiveObject();
  console.log("selectedElement", selectedElement);

  const activeObjects = canvas.getActiveObjects();
  console.log("activeObjects", activeObjects);

  if (!selectedElement) return;

  // if is not activeSelection, return
  if (selectedElement.type !== "activeSelection") return;

  const objects = selectedElement.getObjects(); // to delete from storage

  suppressHistoryRef.current = true; // suppress history while grouping, so that we don't save the intermediate state.

  // to group the selected objects
  const group = selectedElement.toGroup();
  group.set("objectId", uuidv4());
  group.set("consumerLocks", getConsumerLocksObject("group"));
  group.set("layerName", getLayerNameByType("group")); // group layer name

  objects.forEach((item: any) => {
    console.log("item", item);
    // delete from storage later
    deleteShapeFromStorage(item.objectId);
  });

  syncShapeInStorage(group);

  // Refresh the state from here.
  canvas.discardActiveObject();
  canvas.setActiveObject(group);

  canvas.renderAll();

  suppressHistoryRef.current = false; // reset the suppress history flag, so that next operations will be saved in history.
  // if there is a saveStateToHistory function, call it.
  if (saveStateToHistory) {
    saveStateToHistory();
  }
};

export const handleUngroupObjects = ({
  canvas,
  syncShapeInStorage,
  deleteShapeFromStorage,
  suppressHistoryRef,
  saveStateToHistory,
}: any) => {
  if (!canvas) return;

  // Get the selected element. If there is no selected element, return
  const selectedElement = canvas.getActiveObject();

  if (!selectedElement) return;

  // if is not group, return
  if (selectedElement.type !== "group") return;

  suppressHistoryRef.current = true; // suppress history while ungrouping, so that we don't save the intermediate state.

  // Ungroup the selected element
  const groupId = selectedElement.objectId;

  const activeSelection = selectedElement.toActiveSelection();
  const newObjects = activeSelection.getObjects();
  const objectIds = newObjects.map((item: any) => item.objectId);

  // Set coordinates for the selected element and render canvas
  activeSelection.setCoords();

  canvas.discardActiveObject();

  canvas.renderAll();

  // Add the ungrouped objects to the storage
  newObjects.forEach((item: any) => {
    console.log("item from ungroup", item);
    syncShapeInStorage(item);
  });

  // Delete the group from the storage
  deleteShapeFromStorage(groupId);

  // to update state and sidebars.
  // create new selection based on new objects that were ungrouped, from ids.
  // get all from canvas again.
  const allObjs = canvas.getObjects(); // otherwise, will have wrong dimensions and locations.
  const filtered = allObjs.filter((obj: any) =>
    objectIds.includes(obj.objectId)
  );

  const newSelection = new fabric.ActiveSelection(filtered, {
    canvas: canvas,
  });

  newSelection.setCoords();

  canvas.setActiveObject(newSelection);
  canvas.renderAll();

  suppressHistoryRef.current = false; // reset the suppress history flag, so that next operations will be saved in history.

  // if there is a saveStateToHistory function, call it.
  if (saveStateToHistory) {
    saveStateToHistory();
  }
};

export const handleAlignCanvasObject = ({
  canvas,
  key,
  syncShapeInStorage,
  saveStateToHistory,
}: any) => {
  if (!canvas) return;

  const selectedElement = canvas.getActiveObject();
  if (!selectedElement) return;

  // Capture initial positions to check later if they changed
  const initialPosition = {
    left: selectedElement.left,
    top: selectedElement.top,
  };

  const boundingBox = selectedElement.getBoundingRect(true);

  switch (key) {
    case "centerHorizontally":
      selectedElement.set({
        left:
          (canvas.width - boundingBox.width) / 2 -
          (boundingBox.left - selectedElement.left),
      });
      break;

    case "centerVertically":
      selectedElement.set({
        top:
          (canvas.height - boundingBox.height) / 2 -
          (boundingBox.top - selectedElement.top),
      });
      break;

    case "alignToLeft":
      selectedElement.set({
        left: 0 - (boundingBox.left - selectedElement.left),
      });
      break;

    case "alignToTop":
      selectedElement.set({
        top: 0 - (boundingBox.top - selectedElement.top),
      });
      break;

    case "alignToBottom": {
      const offsetTop = canvas.height - boundingBox.height;
      selectedElement.set({
        top: offsetTop - (boundingBox.top - selectedElement.top),
      });
      break;
    }

    case "alignToRight": {
      const offsetLeft = canvas.width - boundingBox.width;
      selectedElement.set({
        left: offsetLeft - (boundingBox.left - selectedElement.left),
      });
      break;
    }

    default:
      break;
  }

  selectedElement.setCoords();
  canvas.renderAll();

  const round = (value: number, precision = 2) =>
    parseFloat(value.toFixed(precision));

  const hasMoved =
    round(selectedElement.left) !== round(initialPosition.left) ||
    round(selectedElement.top) !== round(initialPosition.top);

  if (!hasMoved) {
    return; // No movement → skip syncing and history
  }

  if (selectedElement.type === "activeSelection") {
    selectedElement._objects.forEach((item: any) => {
      syncShapeInStorage(item);
    });
  } else {
    syncShapeInStorage(selectedElement);
  }

  if (saveStateToHistory) {
    saveStateToHistory();
  }
};

export const handleAlignSelectionObject = ({
  canvas,
  key,
  syncShapeInStorage,
  saveStateToHistory,
}: any) => {
  if (!canvas) return;

  const selection = canvas.getActiveObject();
  if (!selection || selection.type !== "activeSelection") return;

  const objects = selection._objects;
  if (!objects.length) return;

  // STEP 1: Save original selection bounding box
  const originalBounds = selection.getBoundingRect(true);

  // STEP 2: Align each object to selection bounding box
  const alignments = {
    alignToLeft: (obj: any) => {
      const objBounds = obj.getBoundingRect(true);
      const offset = obj.left - objBounds.left;
      obj.set({ left: originalBounds.left + offset });
    },

    alignToRight: (obj: any) => {
      const objBounds = obj.getBoundingRect(true);
      const offset = obj.left - objBounds.left;
      const newLeft =
        originalBounds.left + originalBounds.width - objBounds.width + offset;
      obj.set({ left: newLeft });
    },

    centerHorizontally: (obj: any) => {
      const objBounds = obj.getBoundingRect(true);
      const offset = obj.left - objBounds.left;
      const newLeft =
        originalBounds.left +
        originalBounds.width / 2 -
        objBounds.width / 2 +
        offset;
      obj.set({ left: newLeft });
    },

    alignToTop: (obj: any) => {
      const objBounds = obj.getBoundingRect(true);
      const offset = obj.top - objBounds.top;
      obj.set({ top: originalBounds.top + offset });
    },

    alignToBottom: (obj: any) => {
      const objBounds = obj.getBoundingRect(true);
      const offset = obj.top - objBounds.top;
      const newTop =
        originalBounds.top + originalBounds.height - objBounds.height + offset;
      obj.set({ top: newTop });
    },

    centerVertically: (obj: any) => {
      const objBounds = obj.getBoundingRect(true);
      const offset = obj.top - objBounds.top;
      const newTop =
        originalBounds.top +
        originalBounds.height / 2 -
        objBounds.height / 2 +
        offset;
      obj.set({ top: newTop });
    },
  };

  const alignFn = alignments[key as keyof typeof alignments];
  if (!alignFn) return;

  objects.forEach((obj: any) => {
    alignFn(obj);
    obj.setCoords();
    syncShapeInStorage(obj);
  });

  // STEP 3: Reset selection to recalculate bounds, then restore original position
  canvas.discardActiveObject();
  const newSelection = new fabric.ActiveSelection(objects, {
    canvas,
  });

  // Restore original selection position
  newSelection.set({
    left: originalBounds.left,
    top: originalBounds.top,
  });
  newSelection.setCoords();

  canvas.setActiveObject(newSelection);
  canvas.requestRenderAll();

  if (saveStateToHistory) {
    saveStateToHistory();
  }
};
